import type { QueryTypes, ResourceArray } from '@medplum/core';
import { normalizeOperationOutcome } from '@medplum/core';
import type { Bundle, ExtractResource, OperationOutcome, ResourceType } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toSortedCompat } from '../utils/to-sorted-compat';
import type { MedplumSearchQueryKey } from './query-keys';
import { createSearchQueryKey } from './query-keys';
import { useDebouncedValue } from './use-debounced-value';

export type SearchOptions<TData = unknown, TError = OperationOutcome> = {
  /** Optional debounce in milliseconds - debounces query parameter changes before executing search */
  debounceMs?: number;
  /** Optional mapping function to transform the result */
  select?: (data: TData) => TData;
  /** Additional TanStack Query options */
  queryOptions?: Omit<
    UseQueryOptions<TData, TError, TData, MedplumSearchQueryKey>,
    'queryKey' | 'queryFn' | 'select'
  >;
};

const DEFAULT_DEBOUNCE_MS = 500;

/**
 * Normalizes a URL string into a stable query parameters object.
 * Parses the URL and sorts keys to ensure consistent object structure.
 * This ensures React Query treats queries with the same parameters as the same query,
 * even if the original query object reference changes during redirects/re-renders.
 *
 * @param debouncedSearchKey - The debounced URL string from fhirSearchUrl
 * @returns Normalized query parameters object with sorted keys, or undefined if URL is invalid
 */
function normalizeQueryFromUrl(debouncedSearchKey: string | undefined): QueryTypes | undefined {
  // If no search key, return undefined
  if (!debouncedSearchKey) {
    return;
  }

  // Parse the URL to recreate query parameters
  // This ensures the query key is based on the actual URL string, not the object reference
  try {
    const url = new URL(debouncedSearchKey);
    const parameters: Record<string, string> = {};
    // Sort keys to ensure consistent object structure
    const keys = [...url.searchParams.keys()];
    if (new Set(keys).size !== keys.length) {
      return toSortedCompat([...url.searchParams.entries()], ([left], [right]) => left.localeCompare(right));
    }
    const sortedKeys = [...keys];
    sortedKeys.sort();
    for (const key of sortedKeys) {
      parameters[key] = url.searchParams.get(key) || '';
    }
    return parameters;
  } catch {
    // If URL parsing fails, return undefined to disable the query
    return;
  }
}

/**
 * React hook for searching FHIR resources using TanStack Query.
 *
 * This is a convenience hook for calling the MedplumClient.search() method with automatic caching and cache invalidation.
 *
 * @param resourceType - The FHIR resource type to search.
 * @param query - Optional search parameters.
 * @param options - Optional options for configuring the search.
 * @returns TanStack Query result object with data, loading state, and error handling.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSearch('Patient', { name: 'John' });
 * ```
 *
 * @example With mapping function
 * ```tsx
 * const { data } = useSearch('Patient', { name: 'John' }, {
 *   select: (bundle) => ({
 *     patients: bundle.entry?.map(e => e.resource) ?? [],
 *     total: bundle.total
 *   })
 * });
 * ```
 */
export function useSearch<K extends ResourceType, TMapped = Bundle<ExtractResource<K>>>(
  resourceType: K,
  query?: QueryTypes,
  options?: SearchOptions<Bundle<ExtractResource<K>>, OperationOutcome> & {
    select?: (data: Bundle<ExtractResource<K>>) => TMapped;
  },
): UseQueryResult<TMapped, OperationOutcome> {
  const medplum = useMedplum();

  // Create a stable search key from the query parameters
  const searchKey = useMemo(
    () => medplum.fhirSearchUrl(resourceType, query).toString(),
    [medplum, resourceType, query],
  );

  // Debounce the search key to prevent excessive requests during rapid parameter changes
  const [debouncedSearchKey] = useDebouncedValue(
    searchKey,
    options?.debounceMs ?? DEFAULT_DEBOUNCE_MS,
    { leading: true },
  );

  // Normalize the query from the debounced URL string to ensure stable query keys
  const normalizedQuery = useMemo(
    () => normalizeQueryFromUrl(debouncedSearchKey),
    [debouncedSearchKey],
  );

  const queryKey = useMemo(
    () => createSearchQueryKey(resourceType, normalizedQuery, 'search'),
    [resourceType, normalizedQuery],
  );

  return useQuery<Bundle<ExtractResource<K>>, OperationOutcome, TMapped, MedplumSearchQueryKey>({
    queryKey,
    queryFn: async () => {
      try {
        // Use original query to preserve types (e.g., boolean vs string)
        // normalizedQuery is only used for stable cache keys
        return await medplum.search(resourceType, query);
      } catch (error) {
        throw normalizeOperationOutcome(error);
      }
    },
    select: options?.select as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    ...options?.queryOptions,
  });
}

/**
 * React hook for searching for a single FHIR resource using TanStack Query.
 *
 * This is a convenience hook for calling the MedplumClient.searchOne() method with automatic caching and cache invalidation.
 *
 * @param resourceType - The FHIR resource type to search.
 * @param query - Optional search parameters.
 * @param options - Optional options for configuring the search.
 * @returns TanStack Query result object with data, loading state, and error handling.
 *
 * @example
 * ```tsx
 * const { data: patient, isLoading } = useSearchOne('Patient', { identifier: '123' });
 * ```
 *
 * @example With mapping to view model
 * ```tsx
 * const { data: patientViewModel } = useSearchOne('Patient', { id: '123' }, {
 *   select: (patient) => ({
 *     id: patient?.id,
 *     fullName: patient ? `${patient.name?.[0]?.given?.join(' ')} ${patient.name?.[0]?.family}` : undefined,
 *     birthDate: patient?.birthDate
 *   })
 * });
 * ```
 */
export function useSearchOne<K extends ResourceType, TMapped = ExtractResource<K> | null>(
  resourceType: K,
  query?: QueryTypes,
  options?: SearchOptions<ExtractResource<K> | null, OperationOutcome> & {
    select?: (data: ExtractResource<K> | null) => TMapped;
  },
): UseQueryResult<TMapped, OperationOutcome> {
  const medplum = useMedplum();

  // Create a stable search key from the query parameters
  const searchKey = useMemo(
    () => medplum.fhirSearchUrl(resourceType, query).toString(),
    [medplum, resourceType, query],
  );

  // Debounce the search key to prevent excessive requests during rapid parameter changes
  const [debouncedSearchKey] = useDebouncedValue(
    searchKey,
    options?.debounceMs ?? DEFAULT_DEBOUNCE_MS,
    { leading: true },
  );

  // Normalize the query from the debounced URL string to ensure stable query keys
  const normalizedQuery = useMemo(
    () => normalizeQueryFromUrl(debouncedSearchKey),
    [debouncedSearchKey],
  );

  const queryKey = useMemo(
    () => createSearchQueryKey(resourceType, normalizedQuery, 'searchOne'),
    [resourceType, normalizedQuery],
  );

  return useQuery<ExtractResource<K> | null, OperationOutcome, TMapped, MedplumSearchQueryKey>({
    queryKey,
    queryFn: async () => {
      try {
        // Use original query to preserve types (e.g., boolean vs string)
        // normalizedQuery is only used for stable cache keys
        // Convert undefined to null for TanStack Query v5 compatibility
        const result = await medplum.searchOne(resourceType, query);
        // eslint-disable-next-line unicorn/no-null -- TanStack Query v5 compatibility
        return result ?? null;
      } catch (error) {
        throw normalizeOperationOutcome(error);
      }
    },
    select: options?.select as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    ...options?.queryOptions,
  });
}

/**
 * React hook for searching for an array of FHIR resources using TanStack Query.
 *
 * This is a convenience hook for calling the MedplumClient.searchResources() method with automatic caching and cache invalidation.
 *
 * @param resourceType - The FHIR resource type to search.
 * @param query - Optional search parameters.
 * @param options - Optional options for configuring the search.
 * @returns TanStack Query result object with data, loading state, and error handling.
 *
 * @example
 * ```tsx
 * const { data: patients = [], isLoading } = useSearchResources('Patient', { active: true });
 * ```
 *
 * @example With mapping to view models
 * ```tsx
 * const { data: patientViewModels = [] } = useSearchResources('Patient', { active: true }, {
 *   select: (patients) => patients.map(patient => ({
 *     id: patient.id,
 *     displayName: patient.name?.[0]?.text || 'Unknown',
 *     age: calculateAge(patient.birthDate)
 *   }))
 * });
 * ```
 */
export function useSearchResources<K extends ResourceType, TMapped = ResourceArray<ExtractResource<K>>>(
  resourceType: K,
  query?: QueryTypes,
  options?: SearchOptions<ResourceArray<ExtractResource<K>>, OperationOutcome> & {
    select?: (data: ResourceArray<ExtractResource<K>>) => TMapped;
  },
): UseQueryResult<TMapped, OperationOutcome> {
  const medplum = useMedplum();

  // Create a stable search key from the query parameters
  const searchKey = useMemo(
    () => medplum.fhirSearchUrl(resourceType, query).toString(),
    [medplum, resourceType, query],
  );

  // Debounce the search key to prevent excessive requests during rapid parameter changes
  const [debouncedSearchKey] = useDebouncedValue(
    searchKey,
    options?.debounceMs ?? DEFAULT_DEBOUNCE_MS,
    { leading: true },
  );

  // Normalize the query from the debounced URL string to ensure stable query keys
  const normalizedQuery = useMemo(
    () => normalizeQueryFromUrl(debouncedSearchKey),
    [debouncedSearchKey],
  );

  const queryKey = useMemo(
    () => createSearchQueryKey(resourceType, normalizedQuery, 'searchResources'),
    [resourceType, normalizedQuery],
  );

  return useQuery<ResourceArray<ExtractResource<K>>, OperationOutcome, TMapped, MedplumSearchQueryKey>({
    queryKey,
    queryFn: async () => {
      try {
        // Use original query to preserve types (e.g., boolean vs string)
        // normalizedQuery is only used for stable cache keys
        return await medplum.searchResources(resourceType, query);
      } catch (error) {
        throw normalizeOperationOutcome(error);
      }
    },
    select: options?.select as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    ...options?.queryOptions,
  });
}
